import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Tag from 'ext:flarum/tags/common/models/Tag';
import IndexPage from 'flarum/forum/components/IndexPage';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import TagHero from 'ext:flarum/tags/forum/components/TagHero';
import DiscussionListState from 'flarum/forum/states/DiscussionListState';
import TagLinkButton from 'ext:flarum/tags/forum/components/TagLinkButton';
import tagIcon from 'ext:flarum/tags/common/helpers/tagIcon';
import tagsLabel from 'ext:flarum/tags/common/helpers/tagsLabel';
import getSelectableTags from 'ext:flarum/tags/forum/utils/getSelectableTags';
import Stream from 'flarum/common/utils/Stream';
import Button from 'flarum/common/components/Button';
import extractText from 'flarum/common/utils/extractText';
import classList from 'flarum/common/utils/classList';

// ===== 专区配置：后端 ForumZoneFields 下发的 ID（按 ID 判断，前端零名称/slug 字面量） =====
// 后台「扩展设置」里选择专区标签与用户组；改名/改别名/改组名都不影响。
const MODERATORS_GROUP_ID = '4'; // Flarum 默认 Mods 组，恒定值

function zoneTagIdSet() {
  const ids = new Set();
  // 后台下发的完整专区集合（三个角色标签 + 额外多选的专区标签）
  (app.forum.attribute('csZoneTagIds') || []).forEach(id => ids.add(String(id)));
  // 兜底：集合属性缺失时退回三个角色标签单独字段
  ['csSupplierTagId', 'csClientTagId', 'csPublicTagId'].forEach(key => {
    const v = app.forum.attribute(key);
    if (v !== null && v !== undefined) ids.add(String(v));
  });
  return ids;
}
function isZoneTagId(id) {
  return zoneTagIdSet().has(String(id));
}

// 按显示名称判断（用于 DOM 文本）：经 store 反查该标签，再按 ID 判断
function isZoneTagName(name) {
  const v = name && name.trim();
  if (!v) return false;
  const tag = (app.store.all('tags') || []).find(t => (t.name() || '').trim() === v);
  return tag ? isZoneTagId(tag.id()) : false;
}

// ===== 判断函数 =====

function isInGroupId(user, groupId) {
  if (!user || groupId === null || groupId === undefined) return false;
  return (user.groups() || []).some(g => String(g.id()) === String(groupId));
}
function isMod(user) {
  user = user || app.session.user;
  if (!user) return false;
  if (user.isAdmin()) return true;
  return (user.groups() || []).some(g => String(g.id()) === MODERATORS_GROUP_ID);
}
function isSupplier(user) {
  return isInGroupId(user || app.session.user, app.forum.attribute('csSupplierGroupId'));
}
function isClient(user) {
  return isInGroupId(user || app.session.user, app.forum.attribute('csClientGroupId'));
}
function isRestricted() {
  const user = app.session.user;
  if (!user) return false;
  const groups = (user.groups() || []).map(g => g.id() + ':' + g.nameSingular());
  const mod = isMod(user);
  const supp = isSupplier(user);
  const cli = isClient(user);
  const result = !mod && (supp || cli);
  if (!isRestricted._logged) {
    isRestricted._logged = true;
    console.log('[acme-cs] isRestricted check: user=', user.username(), 'groups=', groups, 'mod=', mod, 'supp=', supp, 'cli=', cli, '=>', result);
  }
  return result;
}

// ===== 扩展初始化 =====

app.initializers.add('acme-customer-separation', () => {
  // ---- 0. 受限用户标签数量 ----
  // 受限用户（供应商/客户端）对主标签（专区）无感、选不到主标签；专区标签由
  // 服务端在数量校验之前自动注入请求（见 extend.php 的 before 钩子），所以这里
  // 只需把受限用户自己浏览器会话的「主标签最少」视为 0（前端 UI 不要求他们选
  // 主标签），其余（主标签最多、次级最少/最多）全部沿用全局 Tags 设置。
  // 版主/管理员会话不进入此分支，完全走全局设置。
  // 注意必须用 app.beforeMount：initializer 阶段 app.session / app.forum 还没初始化
  // （boot 顺序是先 initialize 再 store.pushPayload 再设 session/forum），直接读会 undefined。
  app.beforeMount(() => {
    if (isRestricted()) {
      app.forum.pushAttributes({
        minPrimaryTags: 0
      });
    }
  });

  // ---- 0a. 修复 requestParams：产品/市场单选筛选时保留数组 ----
  // flarum/tags 的 extend 会强制把 filter.tag 覆写成 this.params.tags（单值）。
  // 本 extend 在之后执行，若 this.params.filter.tag 是数组则保留数组
  // （单选筛选存的就是单元素数组，保证专区路由标签不把它顶掉）。
  extend(DiscussionListState.prototype, 'requestParams', function (params) {
    var _this$params$filter;
    const tag = (_this$params$filter = this.params.filter) == null ? void 0 : _this$params$filter.tag;
    if (Array.isArray(tag)) {
      params.filter.tag = tag;
    }
  });

  // ---- 0b. 注入网格样式 ----
  // cs-tag-grid--two：受限用户两列（产品/市场）；默认三列（专区/产品/市场）由版主分支用
  const csStyle = document.createElement('style');
  csStyle.textContent = '.cs-tag-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:4px 8px;padding:6px 0}' + '.cs-tag-grid--two{grid-template-columns:1fr 1fr}' + '.cs-tag-col{display:flex;flex-direction:column;height:100%}' + '.cs-tag-col-body{flex:1;display:flex;flex-direction:column;justify-content:space-evenly}' + '.cs-tag-col .TagLinkButton{display:flex!important;width:100%!important;font-size:12px;margin:3px 0;justify-content:flex-start;align-items:center;gap:12px;padding-left:18px}' + '.cs-tag-col-header{text-align:center;font-size:14px;font-weight:400;color:var(--muted-color,#889);padding:4px 0 6px}' + '@media(max-width:991px){.cs-tag-grid{grid-template-columns:1fr 1fr}}' + '@media(max-width:767px){.cs-tag-grid,.cs-tag-grid--two{grid-template-columns:1fr}.cs-tag-col .TagLinkButton{padding-left:200px!important;width:100%!important;box-sizing:border-box!important}}' +
  // 发帖标签选择弹窗：产品/市场上下分区的标题行（li，不可点不可悬停）
  '.SelectTagList-section-header{display:block;text-align:center;font-size:14px;font-weight:400;color:var(--muted-color,#889);padding:8px 0 4px;border-bottom:1px solid var(--control-bg,#e5e7e9);pointer-events:none;cursor:default}';
  document.head.appendChild(csStyle);

  // ---- 0c. 共享工具（版主三列 / 受限两列复用） ----

  // 当前生效的标签筛选（slug 数组）：产品/市场单选的 filter.tag 优先，否则取路由标签
  // （仅用于高亮判断与「再点取消」的比对；单选筛选本身不叠加专区）
  function activeTagSlugs() {
    var _app$discussions$para;
    const filterTag = (_app$discussions$para = app.discussions.params.filter) == null ? void 0 : _app$discussions$para.tag;
    if (Array.isArray(filterTag)) return filterTag;
    if (typeof filterTag === 'string' && filterTag) return [filterTag];
    const routeTag = app.discussions.params.tags;
    return routeTag ? [routeTag] : [];
  }

  // 单选筛选：点产品/市场标签 = 只按这一个标签筛（不叠加专区、不与其他标签组合），
  // 再点同一个已选标签 = 取消筛选（回到当前专区/全部讨论）。
  function toggleFilterTag(t) {
    const current = activeTagSlugs();
    const np = _objectSpread({}, app.discussions.params);
    np.filter = _objectSpread({}, np.filter);
    if (current.length === 1 && current[0] === t.slug()) {
      delete np.filter.tag;
    } else {
      np.filter.tag = [t.slug()];
    }
    app.discussions.refreshParams(np, 1);
  }

  // 当前单选筛选生效的产品/市场标签（未筛选或筛选的是专区标签时返回 null）。
  // 注意只看 filter.tag（点产品/市场标签写入的筛选），不看路由标签——
  // 路由标签（专区）的 hero 由 flarum/tags 原生显示。
  function activeFilterTag() {
    var _app$discussions$para2;
    const filterTag = (_app$discussions$para2 = app.discussions.params.filter) == null ? void 0 : _app$discussions$para2.tag;
    const slugs = Array.isArray(filterTag) ? filterTag : typeof filterTag === 'string' && filterTag ? [filterTag] : [];
    if (slugs.length !== 1) return null;
    const tag = (app.store.all('tags') || []).find(t => t.slug() === slugs[0]);
    if (!tag || isZoneTagId(tag.id())) return null;
    return tag;
  }

  // ---- 0e. 顶部 hero 跟随单选筛选 ----
  // 点专区标签是路由跳转，flarum/tags 原生把顶部 hero 换成对应专区；点产品/市场标签
  // 只是 filter 筛选、路由不动，原生 hero 仍停留在原专区/默认。这里在单选筛选生效时
  // 用所选标签的 TagHero 替换顶部 hero（名称/图标/颜色/描述与专区页一致），
  // 取消筛选后自动恢复原生显示。
  override(IndexPage.prototype, 'hero', function (original) {
    const tag = activeFilterTag();
    if (tag) return m(TagHero, {
      model: tag
    });
    return original();
  });

  // 浏览器标签页标题同样跟随单选筛选
  extend(IndexPage.prototype, 'setTitle', function () {
    const tag = activeFilterTag();
    if (tag) app.setTitle(tag.name());
  });

  // 产品/市场列的标签按钮：点击叠加筛选，不跳转路由
  function filterTagLink(t) {
    const active = activeTagSlugs().indexOf(t.slug()) !== -1;
    const color = t.color();
    return m("a", {
      className: classList('TagLinkButton hasIcon', {
        active
      }),
      href: "javascript:void(0)",
      onclick: e => {
        e.preventDefault();
        toggleFilterTag(t);
      },
      style: color ? {
        '--color': color
      } : {}
    }, tagIcon(t, {
      className: 'Button-icon'
    }), m("span", {
      className: "Button-label"
    }, t.name()));
  }

  // 拉全量标签：flarum/tags 给首页预载的 forum.tags 关系最多只带 4 个次级标签（limit(4)，
  // 原生给「3 个热门+更多」用的），网格需要全部产品/市场标签。load 按缓存去重，
  // 加载完成前先渲染已有数据，完成后 redraw 补全。
  function loadAllTags() {
    if (!app.tagList) return;
    app.tagList.load(['parent']).then(tags => {
      if (tags && tags.length) m.redraw();
    });
  }

  // 三区分类（侧边栏与发帖标签弹窗共用，单一数据源）：
  // 专区 = 所有主标签（position 非空且非子标签）；产品/市场 = 后台「产品标签/市场标签」集合。
  // 同一标签同时勾进产品与市场时只归产品（与设置页 help 文案一致）。
  // 后台扩展设置里增删产品/市场标签或新增主标签（专区）后，这里自动跟随，无需改代码。
  function tagColumns() {
    const allTags = app.store.all('tags') || [];
    const productIds = new Set((app.forum.attribute('csProductTagIds') || []).map(String));
    const marketIds = new Set((app.forum.attribute('csMarketTagIds') || []).map(String));
    return {
      allTags,
      zones: allTags.filter(t => t.position() !== null && !t.isChild()),
      products: allTags.filter(t => productIds.has(String(t.id()))),
      markets: allTags.filter(t => marketIds.has(String(t.id())) && !productIds.has(String(t.id())))
    };
  }

  // ---- 1. 侧边栏 ----
  extend(IndexSidebar.prototype, 'navItems', function (items) {
    let restricted = false;
    try {
      restricted = isRestricted();
    } catch (e) {}

    // 受限用户：两列网格（产品 / 市场，无专区列），标签取自用户绑定。
    // 两个维度逻辑对称：绑定了（非空数组）-> 该列只显示绑定的标签；
    // 未绑定（空数组/未设置）-> 该列显示全部（原始需求：供应商隐含全部市场、客户端隐含全部产品）。
    // 后台编辑用户改绑定 -> 前端属性实时跟随（用户重新进页面即生效）。
    // 版主的 zone 标签项、/tags 总览、「更多」全部移除。
    if (restricted) {
      loadAllTags();
      const user = app.session.user;
      const _tagColumns = tagColumns(),
        products = _tagColumns.products,
        markets = _tagColumns.markets;
      const boundProductIds = new Set((user.attribute('csProductTagIds') || []).map(String));
      const boundMarketIds = new Set((user.attribute('csMarketTagIds') || []).map(String));
      const bindFilter = (tags, boundIds) => boundIds.size > 0 ? tags.filter(t => boundIds.has(String(t.id()))) : tags;
      const productTags = bindFilter(products, boundProductIds);
      const marketTags = bindFilter(markets, boundMarketIds);

      // 清掉原生标签列表（含「更多」截断）与专区相关项
      items.remove('tags');
      items.remove('separator');
      items.remove('moreTags');
      (app.store.all('tags') || []).forEach(t => items.remove('tag' + t.id()));
      if (productTags.length || marketTags.length) {
        items.add('cs-tag-grid', m("div", {
          className: "cs-tag-grid cs-tag-grid--two"
        }, productTags.length > 0 ? m("div", {
          className: "cs-tag-col cs-tag-col-products"
        }, m("div", {
          className: "cs-tag-col-header"
        }, m("i", {
          className: "icon fas fa-box",
          style: "font-size:15px"
        }), " \u4EA7\u54C1"), m("div", {
          className: "cs-tag-col-body"
        }, productTags.map(filterTagLink))) : null, marketTags.length > 0 ? m("div", {
          className: "cs-tag-col cs-tag-col-markets"
        }, m("div", {
          className: "cs-tag-col-header"
        }, m("i", {
          className: "icon fas fa-globe",
          style: "font-size:15px"
        }), " \u5E02\u573A"), m("div", {
          className: "cs-tag-col-body"
        }, marketTags.map(filterTagLink))) : null), -14);
      }
      return;
    }

    // 版主/管理员：三列网格（专区 / 产品 / 市场）代替原有标签
    loadAllTags();
    const _tagColumns2 = tagColumns(),
      allTags = _tagColumns2.allTags,
      zoneTags = _tagColumns2.zones,
      productTags = _tagColumns2.products,
      marketTags = _tagColumns2.markets;
    if (!zoneTags.length && !productTags.length && !marketTags.length) return;
    const params = app.search.state ? app.search.state.stickyParams() : {};
    const currentTag = app.currentTag();

    // 移除 addTagList 添加的全部标签项 + 标签页链接 + 分隔线 + 「更多」链接
    // （/tags 总览页是原生大贴图+标签云排版，与专区/产品/市场分区导航脱节，不让用户掉进去）
    items.remove('tags');
    items.remove('separator');
    items.remove('moreTags');
    allTags.forEach(t => items.remove('tag' + t.id()));

    // 专区列：点击跳转标签页（跳转会重置叠加筛选）
    const zoneLinks = zoneTags.map(t => m(TagLinkButton, {
      model: t,
      params: params,
      active: currentTag === t
    }));
    items.add('cs-tag-grid', m("div", {
      className: "cs-tag-grid"
    }, zoneLinks.length > 0 ? m("div", {
      className: "cs-tag-col cs-tag-col-zones"
    }, m("div", {
      className: "cs-tag-col-header"
    }, m("i", {
      className: "icon fas fa-folder",
      style: "font-size:15px"
    }), " \u4E13\u533A"), m("div", {
      className: "cs-tag-col-body"
    }, zoneLinks)) : null, productTags.length > 0 ? m("div", {
      className: "cs-tag-col cs-tag-col-products"
    }, m("div", {
      className: "cs-tag-col-header"
    }, m("i", {
      className: "icon fas fa-box",
      style: "font-size:15px"
    }), " \u4EA7\u54C1"), m("div", {
      className: "cs-tag-col-body"
    }, productTags.map(filterTagLink))) : null, marketTags.length > 0 ? m("div", {
      className: "cs-tag-col cs-tag-col-markets"
    }, m("div", {
      className: "cs-tag-col-header"
    }, m("i", {
      className: "icon fas fa-globe",
      style: "font-size:15px"
    }), " \u5E02\u573A"), m("div", {
      className: "cs-tag-col-body"
    }, marketTags.map(filterTagLink))) : null), -14);
  });

  // ---- 2. 发帖标签选择弹窗 ----
  // 受限用户：弹窗候选集 = 侧边栏两列同款集合（绑定收窄：绑定了按绑定，没绑显示全部；
  // 专区/未分类标签一律不出现）。弹窗列表分「产品」「市场」上下两区，支持多选。
  // 版主/管理员：候选集保持原生（全部标签），列表分「专区/产品/市场」三区（见下方 content 扩展）。
  // 注意：
  // 1. 不能 extend initAttrs（静态方法，(this.constructor).initAttrs() 静态调用，
  //    prototype 扩展不生效）；挂 oninit 替换 selectableTags 即可--tagList.load 是
  //    异步的，其 then 回调执行时才调 attrs.selectableTags，那时已被替换。
  // 2. extend 回调签名是 callback(返回值, ...原参数)：oninit 无返回值，所以不能
  //    写 function(vnode)（vnode 会拿到 undefined 抛错）；直接读 this.attrs，
  //    它和 vnode.attrs 是同一对象，改它不会被 onbeforeupdate 的 setAttrs 冲掉。
  extend('ext:flarum/tags/common/components/TagSelectionModal', 'oninit', function () {
    if (!isRestricted()) return;
    // 只处理 TagDiscussionModal（发帖/编辑帖打标签）；设置页的 select-tags
    // 复用同一基类但不受此影响（那里 attrs.selectableTags 过滤逻辑不同）
    if (!this.attrs || typeof this.attrs.selectableTags !== 'function') return;
    // TagDiscussionModal 的特征：requireParentTag 为 true
    if (!this.attrs.requireParentTag) return;
    const originalSelectable = this.attrs.selectableTags;
    this.attrs.selectableTags = tags => {
      // 原生过滤（canStartDiscussion / 已在讨论上的标签）先跑
      const base = originalSelectable(tags);
      const _tagColumns3 = tagColumns(),
        products = _tagColumns3.products,
        markets = _tagColumns3.markets;
      const user = app.session.user;
      const boundProductIds = new Set((user.attribute('csProductTagIds') || []).map(String));
      const boundMarketIds = new Set((user.attribute('csMarketTagIds') || []).map(String));
      const bindFilter = (list, boundIds) => boundIds.size > 0 ? list.filter(t => boundIds.has(String(t.id()))) : list;
      const visible = [...bindFilter(products, boundProductIds), ...bindFilter(markets, boundMarketIds)];
      // 与原生过滤结果（canStartDiscussion / 讨论已有标签）取交集
      const baseIds = new Set(base.map(t => String(t.id())));
      return visible.filter(t => baseIds.has(String(t.id())));
    };
  });

  // 弹窗列表分区（版主/管理员与受限用户共用一套逻辑）：
  // - 版主/管理员：专区 / 产品 / 市场 三区（专区 = 所有主标签，与侧边栏专区列一致）。
  // - 受限用户：产品 / 市场 两区（专区标签已被 oninit 的 selectableTags 过滤掉，
  //   zone 区自然不出现）。
  // 普通成员保持原生列表。分区成员与侧边栏同源于 tagColumns()：后台扩展设置里
  // 增删产品/市场标签或新增主标签（专区）后，弹窗自动跟随。
  // content() 返回 [body, footer] 数组，这里拿到 footer 里的 ul，把其中的 li
  // 按标签 ID 归类，插入分区标题 li。
  extend('ext:flarum/tags/common/components/TagSelectionModal', 'content', function (result) {
    // 只处理发帖/编辑帖打标签弹窗（TagDiscussionModal 的特征：requireParentTag 为 true）
    if (!this.attrs || !this.attrs.requireParentTag) return;
    const restricted = isRestricted();
    if (!restricted && !isMod(app.session.user)) return;
    const _tagColumns4 = tagColumns(),
      zones = _tagColumns4.zones,
      products = _tagColumns4.products,
      markets = _tagColumns4.markets;
    const zoneIds = new Set(zones.map(t => String(t.id())));
    const productIds = new Set(products.map(t => String(t.id())));
    const marketIds = new Set(markets.map(t => String(t.id())));

    // result[1] = Modal-footer 的 vnode，其 children = [ul.SelectTagList, (可选 ToggleButton)]
    const footer = result[1];
    if (!footer || !Array.isArray(footer.children)) return;
    const ul = footer.children.find(c => c && c.tag === 'ul');
    if (!ul || !Array.isArray(ul.children)) return;

    // 原生每个 li 的 data-index = 标签 id；按 ID 归类。未分类的次级标签不属于任何
    // 一区，不展示（与侧边栏行为一致）。编辑已有讨论时 selectedTags 里可能含其它
    // 标签，但列表 li 只处理列表内的。
    const liNodes = ul.children;
    const classify = node => {
      const id = node && node.attrs && String(node.attrs['data-index']);
      if (zoneIds.has(id)) return 'zone';
      if (productIds.has(id)) return 'product';
      if (marketIds.has(id)) return 'market';
      return null;
    };
    const zoneLis = liNodes.filter(n => classify(n) === 'zone');
    const productLis = liNodes.filter(n => classify(n) === 'product');
    const marketLis = liNodes.filter(n => classify(n) === 'market');
    if (!zoneLis.length && !productLis.length && !marketLis.length) return;
    const sectionHeader = title => m("li", {
      className: "SelectTagList-section-header"
    }, title);
    ul.children = [...(zoneLis.length ? [sectionHeader('专区'), ...zoneLis] : []), ...(productLis.length ? [sectionHeader('产品'), ...productLis] : []), ...(marketLis.length ? [sectionHeader('市场'), ...marketLis] : [])];
  });

  // ---- 2a. 编辑首帖时可同时修改标题与标签 ----
  // 原生编辑只能改正文；改标题要走讨论页菜单的「重命名」，改标签要走「选择标签」，
  // 两处都藏在右上角下拉里。这里把标题输入框、标签按钮并进「编辑首帖」composer 的
  // 头部，一次提交全部生效：
  // - 标题输入框：仅 discussion.canRename() 时出现（权限与服务端一致）；
  // - 标签按钮：仅 discussion.canTag() 时出现，弹窗复用 flarum/tags 的
  //   TagDiscussionModal（composer 模式：选择不立即保存，随 composer 提交一起生效），
  //   受限用户的候选集过滤照常复用第 2 节的 TagSelectionModal 扩展；
  // - 仅对首帖生效（标题/标签属于讨论而非回帖），编辑回帖时 composer 与原生一致。
  // 注意：oninit 的 extend 回调签名是 (返回值, ...原参数)，oninit 无返回值，
  // 所以不能写 function(vnode)，直接读 this（与第 2 节同理）。
  function editingDiscussion(post) {
    const discussion = post && post.discussion();
    if (!discussion || Number(post.number()) !== 1) return null;
    return discussion;
  }
  function sameTags(a, b) {
    const ids = list => (list || []).map(t => String(t.id())).sort().join(',');
    return ids(a) === ids(b);
  }
  extend('flarum/forum/components/EditPostComposer', 'oninit', function () {
    const discussion = editingDiscussion(this.attrs.post);
    if (!discussion) return;
    if (discussion.canRename()) {
      this.composer.fields.title = Stream(discussion.title());
    }
    if (discussion.canTag()) {
      this.composer.fields.tags = (discussion.tags() || []).slice();
    }

    // 标签选择弹窗（与 flarum/tags 给 DiscussionComposer 挂的 chooseTags 同款）。
    // TagDiscussionModal 在 flarum/tags 里是懒加载 chunk（未随主包注册进 flarum.reg），
    // 静态导入拿到的是 undefined，必须走动态 import 才能触发 chunk 加载。
    this.chooseTags = () => {
      if (!getSelectableTags(discussion).length) return;
      app.modal.show(() => flarum.reg.asyncModuleImport('ext:flarum/tags/forum/components/TagDiscussionModal'), {
        selectedTags: (this.composer.fields.tags || []).slice(0),
        onsubmit: tags => {
          this.composer.fields.tags = tags;
          this.$('textarea').focus();
        }
      });
    };
  });
  extend('flarum/forum/components/EditPostComposer', 'headerItems', function (items) {
    const discussion = editingDiscussion(this.attrs.post);
    if (!discussion) return;
    if (this.composer.fields.title) {
      items.add('discussionTitle', m("h3", null, m("input", {
        className: "FormControl DiscussionComposer-title",
        bidi: this.composer.fields.title,
        placeholder: extractText(app.translator.trans('core.forum.composer_discussion.title_placeholder')),
        onkeydown: e => {
          if (e.which === 13) {
            e.preventDefault();
            this.composer.editor.moveCursorTo(0);
          }
          e.redraw = false;
        }
      })), 5);
    }
    if (this.composer.fields.tags) {
      const tags = this.composer.fields.tags;
      // 优先级 4：排在标题输入框（5）之后、原生的「讨论…中的第N篇」链接（0）之前，
      // 让标签按钮贴在输入框右侧，不与链接文字同排（li 有 margin-right:-4px 会压字）。
      items.add('tags', m(Button, {
        className: "Button Button--ua-reset DiscussionComposer-changeTags",
        onclick: () => this.chooseTags && this.chooseTags()
      }, tags.length ? tagsLabel(tags) : m("span", {
        className: "TagLabel untagged"
      }, app.translator.trans('flarum-tags.forum.composer_discussion.choose_tags_link'))), 4);
    }
  });

  // 标题/标签有改动时也算「有未保存修改」（关闭确认、离开页面提示）
  extend('flarum/forum/components/EditPostComposer', 'hasChanges', function (result) {
    const discussion = editingDiscussion(this.attrs.post);
    if (!discussion) return result;
    if (result) return true;
    const fields = this.composer.fields;
    if (fields.title && fields.title() !== discussion.title()) return true;
    if (fields.tags && !sameTags(fields.tags, discussion.tags())) return true;
    return false;
  });

  // 提交：标题/标签有改动时先保存讨论（title + tags 关系一次提交），
  // 成功后再走原生正文保存流程（滚动定位、成功提示、收起 composer 都归它管）；
  // 讨论保存失败时调 loaded 复位转圈，用户留在 composer 里可重试。
  override('flarum/forum/components/EditPostComposer', 'onsubmit', function (original) {
    const discussion = editingDiscussion(this.attrs.post);
    const fields = this.composer.fields;
    if (!discussion || !fields.title && !fields.tags) return original();
    const titleChanged = fields.title && fields.title() !== discussion.title();
    const tagsChanged = fields.tags && !sameTags(fields.tags, discussion.tags());
    if (!titleChanged && !tagsChanged) return original();
    const data = {};
    if (titleChanged) data.title = fields.title();
    if (tagsChanged) data.relationships = {
      tags: fields.tags
    };
    this.loading = true;
    discussion.save(data).then(() => original(), this.loaded.bind(this));
  });

  // ---- 3. 顺延执行的受限用户专有功能 ----
  setTimeout(() => {
    var _app$session$user;
    // 发帖选择器：专区标签不可选（仅对受限用户生效）
    override(Tag.prototype, 'canStartDiscussion', function (original) {
      if (isRestricted() && (isZoneTagId(this.id()) || isZoneTagName(this.name()))) return false;
      return original();
    });

    // 动态检查 isRestricted 的 DOM 隐藏（应对用户组延迟加载）
    function hideZoneTagElements() {
      if (!isRestricted()) return;
      // 调试：每 30 次打印一次
      if (!hideZoneTagElements._cnt) hideZoneTagElements._cnt = 0;
      hideZoneTagElements._cnt++;
      const log = hideZoneTagElements._cnt <= 3 || hideZoneTagElements._cnt % 30 === 0;
      const tagLabels = document.querySelectorAll('.TagLabel');
      if (log) console.log('[acme-cs] TagLabel count:', tagLabels.length);
      tagLabels.forEach(el => {
        if (el.style.display === 'none') return;
        const nameEl = el.querySelector('.TagLabel-name');
        if (nameEl && isZoneTagName(nameEl.textContent.trim())) {
          if (log) console.log('[acme-cs] 隐藏 TagLabel:', nameEl.textContent.trim());
          el.style.display = 'none';
        }
      });
      const tagLinks = document.querySelectorAll('.TagLinkButton, a[href*="/t/"]');
      if (log) console.log('[acme-cs] TagLinkButton count:', tagLinks.length);
      tagLinks.forEach(el => {
        if (el.style.display === 'none') return;
        const text = el.textContent.trim();
        if (log && text && text.length < 20) console.log('[acme-cs] TagLinkButton text:', text);
        if (isZoneTagName(text)) {
          if (log) console.log('[acme-cs] 隐藏 TagLinkButton:', text);
          el.style.display = 'none';
        }
      });
    }
    hideZoneTagElements();
    const observer = new MutationObserver(hideZoneTagElements);
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    setInterval(hideZoneTagElements, 1000);
    console.log('[acme-cs] 已加载, user=', (_app$session$user = app.session.user) == null ? void 0 : _app$session$user.username(), 'isRestricted=', isRestricted());
  }, 0);
});